import {
  createResourceOrgFixture,
  ORG_DEPARTMENT_IDS,
  ORG_LEGAL_ENTITY_IDS,
  orgResourceId,
  RESOURCE_TYPE_PERSON,
  SHIP_CUSTOMERS,
  SHIP_NODE_IDS,
  SHIP_PEOPLE,
} from '@timelog/pm-shared';

/**
 * Project details' sample data — v1 Settings' (docs/briefs/settings-tab.md §5), kept.
 *
 * Not a second data set: it is built ON the prototype's fixtures — the
 * resource organisation's two legal entities, department tree and people
 * (`resource-org.ts`), and Project Ship's people and task ids
 * (`project-ship.ts`) — and only adds the ~170 extra employees the reach
 * numbers need. Neither source is modified, so Planning and Financials see
 * exactly what they did. Deterministic: no randomness, stable ids, so every
 * count in §4 is the same on every load and in every test.
 */

export type SettingsLegalEntity = { id: string; name: string };

export type SettingsDepartment = { id: string; name: string; parentId: string | null };

export type SettingsEmployee = {
  id: string;
  name: string;
  legalEntityId: string;
  departmentId: string;
};

export type SettingsOwners = {
  legalEntityId: string;
  departmentId: string;
  projectManagerId: string;
  accountManagerId: string;
  partnerId: string;
};

export type SettingsContact = { id: string; name: string; title: string };

export type SettingsSample = {
  legalEntities: SettingsLegalEntity[];
  departments: SettingsDepartment[];
  employees: SettingsEmployee[];
  /** The project team, by employee id (~14). */
  projectTeam: string[];
  /** Employee id → the Project Ship task ids they are allocated to (~10 people). */
  allocations: Record<string, string[]>;
  defaultOwners: SettingsOwners;
  projectTypes: string[];
  projectCategories: string[];
  /** Customer tlpId → contacts; `default` for a customer the list does not know. */
  contacts: Record<string, SettingsContact[]>;
};

export const SETTINGS_EMPLOYEE_COUNT = 200;

const E = ORG_LEGAL_ENTITY_IDS;
const D = ORG_DEPARTMENT_IDS;

/** The org fixture's people, by their position there (1-based, `orgResourceId`). */
const ORG = {
  metteSorensen: orgResourceId(1),
  astridVinter: orgResourceId(5),
  lucasNorgaard: orgResourceId(8),
  idaMansson: orgResourceId(9),
  claraEjlersen: orgResourceId(11),
  williamToft: orgResourceId(12),
  oscarLindegaard: orgResourceId(14),
  viktorAagaard: orgResourceId(16),
  majaEnevoldsen: orgResourceId(17),
  felixHoffmann: orgResourceId(22),
} as const;

/**
 * Project Ship's six people, placed in the org. The plan carries no
 * department, so these placements are Settings' own. Mette Sørensen is in
 * both fixtures; she is one person here, under Project Ship's id.
 */
const SHIP_PLACEMENTS: { id: string; name: string; departmentId: string }[] = [
  {
    id: SHIP_PEOPLE.anders!.tlpId,
    name: SHIP_PEOPLE.anders!.snapshot.name,
    departmentId: D.delivery,
  },
  { id: SHIP_PEOPLE.mette!.tlpId, name: SHIP_PEOPLE.mette!.snapshot.name, departmentId: D.backend },
  { id: SHIP_PEOPLE.jonas!.tlpId, name: SHIP_PEOPLE.jonas!.snapshot.name, departmentId: D.backend },
  {
    id: SHIP_PEOPLE.sofia!.tlpId,
    name: SHIP_PEOPLE.sofia!.snapshot.name,
    departmentId: D.frontend,
  },
  { id: SHIP_PEOPLE.lars!.tlpId, name: SHIP_PEOPLE.lars!.snapshot.name, departmentId: D.frontend },
  { id: SHIP_PEOPLE.nina!.tlpId, name: SHIP_PEOPLE.nina!.snapshot.name, departmentId: D.platform },
];

const FIRST_NAMES = [
  'Anne',
  'Kirsten',
  'Hanne',
  'Lone',
  'Susanne',
  'Camilla',
  'Louise',
  'Line',
  'Julie',
  'Emma',
  'Sofie',
  'Laura',
  'Karoline',
  'Signe',
  'Nanna',
  'Rikke',
  'Pernille',
  'Birgitte',
  'Marianne',
  'Tina',
  'Helle',
  'Inge',
  'Johanne',
  'Ellen',
  'Agnes',
  'Vibeke',
  'Gitte',
  'Dorthe',
  'Bente',
  'Lærke',
  'Asta',
  'Josefine',
  'Cecilie',
  'Mathilde',
  'Katrine',
  'Jens',
  'Peter',
  'Michael',
  'Henrik',
  'Søren',
  'Thomas',
  'Martin',
  'Rasmus',
  'Mads',
  'Mikkel',
  'Frederik',
  'Christian',
  'Kasper',
  'Niels',
  'Jesper',
  'Morten',
  'Kim',
  'Bo',
  'Torben',
  'Ole',
  'Poul',
  'Mathias',
  'Magnus',
  'Malthe',
  'Oskar',
  'Carl',
  'Villads',
  'Asger',
  'Esben',
  'Troels',
  'Bjarke',
  'Sune',
  'Aske',
  'Kristian',
  'Steffen',
];

const LAST_NAMES = [
  'Nielsen',
  'Jensen',
  'Hansen',
  'Pedersen',
  'Andersen',
  'Christensen',
  'Larsen',
  'Rasmussen',
  'Jørgensen',
  'Petersen',
  'Madsen',
  'Kristensen',
  'Olsen',
  'Thomsen',
  'Christiansen',
  'Johansen',
  'Møller',
  'Mortensen',
  'Knudsen',
  'Jakobsen',
  'Mikkelsen',
  'Frederiksen',
  'Lauridsen',
  'Henriksen',
  'Laursen',
  'Kjær',
  'Bach',
  'Lund',
  'Dalgaard',
  'Bertelsen',
  'Søgaard',
  'Overgaard',
  'Kjeldsen',
  'Thygesen',
  'Vinther',
  'Bundgaard',
  'Lindhardt',
  'Østergaard',
  'Iversen',
  'Nygaard',
  'Damgaard',
  'Brøndum',
  'Krogh',
  'Fogh',
  'Hedegaard',
  'Juhl',
  'Skovgaard',
  'Winther',
  'Ebbesen',
  'Højgaard',
];

/** Some Danes carry two surnames; every seventh person here does. */
const MIDDLE_NAMES = ['Bach', 'Holm', 'Lund', 'Krogh', 'Vinther', 'Kjær', 'Fogh'];

/** Where the extra people sit: weighted so Engineering and Consulting are the big ones. */
const DEPARTMENT_CYCLE = [
  D.backend,
  D.frontend,
  D.delivery,
  D.platform,
  D.advisory,
  D.backend,
  D.operations,
  D.frontend,
  D.delivery,
  D.engineering,
  D.advisory,
  D.consulting,
];

export const settingsEmployeeId = (n: number): string =>
  `settings-emp-${String(n).padStart(3, '0')}`;

/** Unique, deterministic Danish names, none colliding with `taken`. */
const generateNames = (count: number, taken: Set<string>): string[] => {
  const names: string[] = [];
  for (let i = 0; names.length < count; i += 1) {
    const first = FIRST_NAMES[(i * 13) % FIRST_NAMES.length]!;
    const last = LAST_NAMES[(i * 7 + Math.floor(i / LAST_NAMES.length)) % LAST_NAMES.length]!;
    const middle = i % 7 === 3 ? `${MIDDLE_NAMES[i % MIDDLE_NAMES.length]!} ` : '';
    const name = `${first} ${middle}${last}`;
    if (middle !== '' && middle.trim() === last) continue;
    if (taken.has(name)) continue;
    taken.add(name);
    names.push(name);
  }
  return names;
};

export const createSettingsSample = (): SettingsSample => {
  const org = createResourceOrgFixture();

  const fromShip: SettingsEmployee[] = SHIP_PLACEMENTS.map((person) => ({
    ...person,
    legalEntityId: E.nordicAs,
  }));
  const shipNames = new Set(fromShip.map((person) => person.name));
  const fromOrg: SettingsEmployee[] = org.resources
    .filter((resource) => resource.resourceType === RESOURCE_TYPE_PERSON)
    .filter((resource) => !shipNames.has(resource.name))
    .map(({ id, name, legalEntityId, departmentId }) => ({
      id,
      name,
      legalEntityId,
      departmentId,
    }));

  const known = [...fromShip, ...fromOrg];
  const extra = generateNames(
    SETTINGS_EMPLOYEE_COUNT - known.length,
    new Set(known.map((person) => person.name)),
  ).map((name, index): SettingsEmployee => ({
    id: settingsEmployeeId(index + 1),
    name,
    // About one in five works for the GmbH.
    legalEntityId: index % 5 === 4 ? E.nordicGmbh : E.nordicAs,
    departmentId: DEPARTMENT_CYCLE[index % DEPARTMENT_CYCLE.length]!,
  }));

  const T = SHIP_NODE_IDS;
  const ship = SHIP_PEOPLE;
  // The four people Project Ship's plan staffs keep their task there
  // (Solution design, QA & testing); the rest is Settings' own.
  const allocations: Record<string, string[]> = {
    [ship.anders!.tlpId]: [T.workshops, T.solutionDesign],
    [ship.mette!.tlpId]: [T.solutionDesign, T.apiIntegration],
    [ship.jonas!.tlpId]: [T.solutionDesign, T.apiIntegration],
    [ship.sofia!.tlpId]: [T.frontendImplementation],
    [ship.lars!.tlpId]: [T.qaTesting],
    [ship.nina!.tlpId]: [T.deployment],
    [ORG.astridVinter]: [T.apiIntegration],
    [ORG.lucasNorgaard]: [T.frontendImplementation],
    [ORG.idaMansson]: [T.frontendImplementation, T.workshops],
    [ORG.williamToft]: [T.qaTesting],
  };

  return {
    legalEntities: org.legalEntities.map(({ id, name }) => ({ id, name })),
    departments: org.departments.map(({ id, name, parentId }) => ({ id, name, parentId })),
    employees: [...known, ...extra],
    // The ten allocated, plus four on the team with no task yet — one of
    // them in the GmbH, so "Everyone in [legal entity]" does not cover the team.
    projectTeam: [
      ...Object.keys(allocations),
      ORG.claraEjlersen,
      ORG.viktorAagaard,
      ORG.majaEnevoldsen,
      ORG.felixHoffmann,
    ],
    allocations,
    defaultOwners: {
      legalEntityId: E.nordicAs,
      departmentId: D.consulting,
      projectManagerId: ship.anders!.tlpId,
      accountManagerId: ORG.viktorAagaard,
      partnerId: ORG.oscarLindegaard,
    },
    projectTypes: ['Implementation', 'Consulting', 'Support & maintenance', 'Internal'],
    projectCategories: ['Customer project', 'Internal project', 'Pre-sales'],
    contacts: {
      [SHIP_CUSTOMERS[0]!.tlpId]: [
        { id: 'contact-maersk-1', name: 'Karen Mikkelsen', title: 'Head of IT' },
        { id: 'contact-maersk-2', name: 'Peter Bach', title: 'Procurement manager' },
        { id: 'contact-maersk-3', name: 'Line Østergaard', title: 'Product owner' },
      ],
      default: [
        { id: 'contact-default-1', name: 'Hanne Kjeldsen', title: 'CFO' },
        { id: 'contact-default-2', name: 'Thomas Lund', title: 'IT manager' },
        { id: 'contact-default-3', name: 'Signe Damgaard', title: 'Project sponsor' },
      ],
    },
  };
};
